import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import html2pdf from "html2pdf.js";
import { apiCall } from "../api";
import { can, currentFYKey, SC, SL, SC_OVERDUE } from "../config";
import { Input, Btn } from "./common/FormElements";

// The calibration plan the plant keeps as QA-F-37: instruments down the side, the twelve
// months of the financial year across the top, a mark in the month each falls due.
//
// Deliberately built to the same shape as the PM Plan — same legend, same filters, same
// cell marks and colours — because it is the same kind of document and the people reading
// one read the other. A second visual language for the same idea is just another thing to
// learn.
//
// The rows are derived from the instrument register on the server rather than stored as
// their own schedule: an instrument's calibration due date IS its plan. That is what stops
// this drifting from the master list the way two separate spreadsheets do.

// Overdue is not a status of its own on the server's cell; it is a planned month that has
// gone by. Shown exactly as the PM Plan shows it — the planned mark, ringed in red.
const MARK = { PLAN: "▬", OVERDUE: "▬", DONE: "✓", DELAYED: "!", CANCELLED: "✕" };

function PlanEditModal({ row, onSave, onClose, saving }) {
  const [form, setForm] = useState({
    calibrationFrequency: row.frequency || "",
    lastCalibrationDate: row.lastCalibrationDate || "",
    calibrationDueDate: row.calibrationDueDate || "",
    dept: row.dept || "",
    calibrationLab: row.calibrationLab || "",
    issuedTo: row.issuedTo || "",
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <div>
            <h3 className="font-bold text-gray-800">{row.name}</h3>
            <p className="text-xs text-gray-500 font-mono">{row.instrumentNo}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none" data-testid="plan-edit-close">&times;</button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-gray-500">
            The plan follows the instrument. Changing its due date moves the mark on this row,
            and the register shows the same thing.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Calibration Frequency" value={form.calibrationFrequency}
              onChange={e => set("calibrationFrequency", e.target.value)} placeholder="e.g. 1 YEAR" />
            <Input label="Location / Section" value={form.dept} onChange={e => set("dept", e.target.value)} />
            <Input label="Last Calibration Done On" type="date" value={form.lastCalibrationDate}
              onChange={e => set("lastCalibrationDate", e.target.value)} />
            <Input label="Next Calibration Due On" type="date" value={form.calibrationDueDate}
              onChange={e => set("calibrationDueDate", e.target.value)} />
            <Input label="Calibration Lab" value={form.calibrationLab}
              onChange={e => set("calibrationLab", e.target.value)} />
            <Input label="Issued To" value={form.issuedTo} onChange={e => set("issuedTo", e.target.value)} />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t">
          <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn onClick={() => onSave(form)} disabled={saving} testId="plan-edit-save">
            {saving ? "Saving..." : "Save Changes"}
          </Btn>
        </div>
      </div>
    </div>
  );
}

function CalCell({ cell }) {
  if (!cell) return <span className="text-gray-200">&ndash;</span>;
  const overdue = cell.status === "OVERDUE";
  const tone = overdue ? SC_OVERDUE : SC[cell.status] || SC.PLAN;
  const title = overdue
    ? `Overdue — calibration was due ${cell.date || "this month"} and has not been recorded`
    : cell.status === "DELAYED"
      ? `Calibrated ${cell.date}, but not in the month it was due`
      : cell.status === "DONE"
        ? `Calibrated ${cell.date}${cell.by ? ` by ${cell.by}` : ""}`
        : `Due ${cell.date || ""}`;
  return (
    <div className="relative group inline-flex items-center gap-0.5">
      <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${tone}`} title={title}
        data-testid={overdue ? "cal-overdue" : undefined}>
        {MARK[cell.status] || MARK.PLAN}
      </span>
      {(cell.status === "DONE" || cell.status === "DELAYED") && cell.by && (
        <div className="hidden group-hover:block absolute z-30 bottom-full left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap mb-1 pointer-events-none" data-testid="cal-tooltip">
          {cell.by} &middot; {cell.date}
        </div>
      )}
    </div>
  );
}

export default function CalibrationPlan({ user, unitId }) {
  const [fyFilter, setFyFilter] = useState(currentFYKey());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [locationFilter, setLocationFilter] = useState("ALL");
  const [freqFilter, setFreqFilter] = useState("ALL");
  const [labFilter, setLabFilter] = useState("ALL");
  const [exportingPDF, setExportingPDF] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const gridRef = useRef(null);

  const load = useCallback(async () => {
    if (!unitId) return;
    setLoading(true);
    setError("");
    try {
      setData(await apiCall("GET", `/instruments/${unitId}/calibration-plan?fy=${fyFilter}`));
    } catch (err) {
      setData(null);
      setError(err?.response?.data?.detail || err.message || "Could not load the calibration plan");
    } finally {
      setLoading(false);
    }
  }, [unitId, fyFilter]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => data?.rows || [], [data]);
  const months = data?.months || [];

  // Filter choices come from the instruments actually on the plan, so a lab or a location
  // that no longer appears is not offered.
  const locations = useMemo(
    () => ["ALL", ...[...new Set(rows.map(r => r.dept).filter(Boolean))].sort()], [rows]);
  const frequencies = useMemo(
    () => ["ALL", ...[...new Set(rows.map(r => r.frequency).filter(Boolean))].sort()], [rows]);
  const labs = useMemo(
    () => ["ALL", ...[...new Set(rows.map(r => r.calibrationLab).filter(Boolean))].sort()], [rows]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return rows.filter(r =>
      (locationFilter === "ALL" || r.dept === locationFilter)
      && (freqFilter === "ALL" || r.frequency === freqFilter)
      && (labFilter === "ALL" || r.calibrationLab === labFilter)
      && (!q || (r.name || "").toLowerCase().includes(q)
             || (r.instrumentNo || "").toLowerCase().includes(q)
             || (r.issuedTo || "").toLowerCase().includes(q)));
  }, [rows, searchQuery, locationFilter, freqFilter, labFilter]);

  const availableFYs = useMemo(() => {
    const here = currentFYKey();
    const start = parseInt(here.split("-")[0], 10);
    const out = [];
    for (let y = start - 2; y <= start + 2; y++) out.push(`${y}-${String(y + 1).slice(-2)}`);
    return out;
  }, []);

  async function handleSaveEdit(form) {
    setSavingEdit(true);
    try {
      await apiCall("PUT", `/instruments/${unitId}/${editRow.instrumentId}`, form);
      setEditRow(null);
      await load();
    } catch (err) {
      alert("Could not save: " + (err?.response?.data?.detail || err.message));
    } finally {
      setSavingEdit(false);
    }
  }

  // Deleting a row here is not like deleting a PM plan row, which leaves the machine
  // alone: this row IS the instrument. The confirmation says so plainly rather than
  // letting someone tidy up a plan and lose an instrument's whole record with it.
  async function handleDelete(row) {
    if (!window.confirm(
      `Remove ${row.name} (${row.instrumentNo}) from the register?\n\n` +
      "This is not just a line on this plan — the instrument and its history card go with it.\n\n" +
      "This cannot be undone.")) return;
    try {
      await apiCall("DELETE", `/instruments/${unitId}/${row.instrumentId}`);
      await load();
    } catch (err) {
      alert("Could not delete: " + (err?.response?.data?.detail || err.message));
    }
  }

  async function handleExportPDF() {
    setExportingPDF(true);
    await new Promise(r => setTimeout(r, 300));
    const el = gridRef.current;
    if (!el) { setExportingPDF(false); return; }
    try {
      await html2pdf().set({
        margin: [5, 3, 5, 3],
        filename: `Calibration-Plan_FY${fyFilter}_${unitId}_${new Date().toISOString().split("T")[0]}.pdf`,
        image: { type: "jpeg", quality: 1 },
        html2canvas: { scale: 1.2, useCORS: true, logging: false, width: el.scrollWidth, windowWidth: el.scrollWidth },
        jsPDF: { unit: "mm", format: "a3", orientation: "landscape" },
      }).from(el).save();
    } catch (err) {
      alert("Failed to export PDF.");
    } finally {
      setExportingPDF(false);
    }
  }

  const filtersOn = searchQuery || locationFilter !== "ALL" || freqFilter !== "ALL" || labFilter !== "ALL";
  // Gated the same way the Instrument List gates its own Edit and Del, so a role that can
  // change an instrument there can change it here and nowhere else gains the right.
  const canEdit = can(user, "editMachines");

  return (
    <div className="space-y-4" data-testid="calibration-plan">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-bold text-gray-800">Instruments / Equipments Calibration Plan &middot; FY {fyFilter}</h2>
          <p className="text-xs text-gray-500">
            QA-F-37 &middot; derived from the instrument master list, so the two cannot disagree
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500 font-medium">Financial Year</label>
            <select className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white font-semibold"
              value={fyFilter} onChange={e => setFyFilter(e.target.value)} data-testid="cal-fy-select">
              {availableFYs.map(fy => <option key={fy} value={fy}>FY {fy}</option>)}
            </select>
          </div>
          <button onClick={handleExportPDF} disabled={exportingPDF || !rows.length}
            className="border border-red-500 text-red-600 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-red-50 disabled:opacity-50 whitespace-nowrap"
            data-testid="cal-export-pdf-btn">
            {exportingPDF ? "Exporting..." : "Export PDF"}
          </button>
        </div>
      </div>

      <div className="flex gap-3 flex-wrap text-xs">
        {Object.entries(SL).map(([k, v]) => <span key={k} className={`px-2 py-0.5 rounded font-semibold ${SC[k]}`}>{v}</span>)}
        <span className={`px-2 py-0.5 rounded font-semibold ${SC_OVERDUE}`}
          title="The month it was due has passed and no calibration has been recorded — it stays on the due list until it is done">Overdue</span>
        {!can(user, "editPM") && <span className="text-gray-400">&middot; Read-only for your role</span>}
      </div>

      <div className="flex flex-wrap gap-3 items-end" data-testid="cal-filters">
        <div className="flex-1 min-w-48">
          <label className="text-xs text-gray-500 font-medium block mb-1">Search Instrument</label>
          <input className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Name, ID or who holds it..."
            value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-testid="cal-search" />
        </div>
        <div>
          <label className="text-xs text-gray-500 font-medium block mb-1">Location / Section</label>
          <select className="border rounded-lg px-3 py-2 text-sm bg-white min-w-56" value={locationFilter}
            onChange={e => setLocationFilter(e.target.value)} data-testid="cal-location-filter">
            {locations.map(l => <option key={l}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500 font-medium block mb-1">Calibration Frequency</label>
          <select className="border rounded-lg px-3 py-2 text-sm bg-white" value={freqFilter}
            onChange={e => setFreqFilter(e.target.value)} data-testid="cal-freq-filter">
            {frequencies.map(f => <option key={f}>{f}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500 font-medium block mb-1">Calibration Lab</label>
          <select className="border rounded-lg px-3 py-2 text-sm bg-white" value={labFilter}
            onChange={e => setLabFilter(e.target.value)} data-testid="cal-lab-filter">
            {labs.map(l => <option key={l}>{l}</option>)}
          </select>
        </div>
        {filtersOn && (
          <button onClick={() => { setSearchQuery(""); setLocationFilter("ALL"); setFreqFilter("ALL"); setLabFilter("ALL"); }}
            className="border border-red-300 text-red-500 px-3 py-2 rounded-lg text-sm hover:bg-red-50 mb-0.5"
            data-testid="cal-clear-filters">Clear</button>
        )}
      </div>

      <div className="text-xs text-gray-400" data-testid="cal-showing">
        Showing {filtered.length} of {rows.length} instruments &middot; FY {fyFilter}
        {data?.counts?.TOTAL != null && <> &middot; {data.counts.TOTAL} calibrations fall due</>}
        {data?.counts?.notDueThisYear > 0 && <> &middot; {data.counts.notDueThisYear} next due in a later year</>}
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">Loading calibration plan&hellip;</div>
      ) : error ? (
        <div className="text-sm text-red-600" data-testid="cal-error">{error}</div>
      ) : !rows.length ? (
        <div className="text-sm text-gray-600" data-testid="cal-empty">
          No instruments on the register for this unit yet.
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden" ref={gridRef}>
          {/* The header row stays put while the grid scrolls. The cap is lifted during PDF
              export, otherwise only the rows on screen would be captured. */}
          <div className={exportingPDF ? "overflow-x-auto" : "overflow-auto max-h-[70vh]"}>
            <table className="w-full text-xs min-w-[900px]" data-testid="calibration-plan-table">
              <thead>
                <tr className="bg-gray-50 border-b">
                  {["#", "Instrument", "ID", "Location", "Freq."].map(h => (
                    <th key={h} className="text-left px-3 py-2 text-gray-500 font-semibold sticky top-0 z-20 bg-gray-50">{h}</th>
                  ))}
                  {months.map(m => (
                    <th key={m} className={`px-2 py-2 font-semibold text-center whitespace-nowrap sticky top-0 z-20 bg-gray-50 ${m === data.currentMonth ? "text-blue-600" : "text-gray-500"}`}>{m}</th>
                  ))}
                  <th className="text-left px-3 py-2 text-gray-500 font-semibold sticky top-0 z-20 bg-gray-50">Remarks</th>
                  {canEdit && <th className="px-3 py-2 sticky top-0 z-20 bg-gray-50" />}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr key={r.instrumentId} className="border-b hover:bg-gray-50" data-testid={`cal-row-${r.instrumentNo}`}>
                    <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                    <td className="px-3 py-2 font-medium text-gray-800 whitespace-nowrap">
                      {r.name}
                      {!r.dueMonth && (
                        <span className="ml-1.5 px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded text-[10px] font-semibold"
                          title={`Next due ${r.calibrationDueDate || "unknown"} — outside this financial year`}
                          data-testid={`cal-notdue-${r.instrumentNo}`}>NOT DUE</span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-blue-600 whitespace-nowrap text-xs">{r.instrumentNo}</td>
                    <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{r.dept}</td>
                    <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{r.frequency}</td>
                    {months.map(m => (
                      <td key={m} className="px-1 py-2 text-center" data-testid={`cal-cell-${r.instrumentNo}-${m}`}>
                        <CalCell cell={r.schedule[m]} />
                      </td>
                    ))}
                    <td className="px-3 py-2 text-gray-500 max-w-56">{r.remarks}</td>
                    {canEdit && (
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button onClick={() => setEditRow(r)}
                          className="text-xs text-blue-600 hover:text-blue-800 hover:underline mr-3"
                          data-testid={`plan-edit-${r.instrumentNo}`}>Edit</button>
                        <button onClick={() => handleDelete(r)}
                          className="text-xs text-red-600 hover:text-red-800 hover:underline"
                          data-testid={`plan-delete-${r.instrumentNo}`}>Delete</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editRow && (
        <PlanEditModal row={editRow} saving={savingEdit} onSave={handleSaveEdit}
          onClose={() => setEditRow(null)} />
      )}
    </div>
  );
}
